import * as yup from "yup";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { Images } from "../../../../../config/images";
import TopImage from "../../../common/TopImage";
import CustomInput from "../../../../common/CustomInput";
import CustomSelect from "../../../../common/CustomSelect";
import "../../../SchoolAdmin/Teacher/Teacher.css";
import "../../../../../common/styles/table.css";
import { useEffect, useState, useRef } from "react";
import BackButton from "../../../../common/BackButton";
import { useCreateMember } from "../../../../../hooks/organization/members/useCreateMember";
import { useGetCreatableRoles } from "../../../../../hooks/organization/members/useGetCreatableRoles";
import SuccessModal from "../../../../common/SuccessModal";
import { Button } from "react-bootstrap";

const AddMember = () => {
  const navigate = useNavigate();
  const formikRef = useRef(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    loading: createLoading,
    error: createError,
    createMember,
  } = useCreateMember();

  const {
    roles,
    loading: rolesLoading,
    error: rolesError,
    getCreatableRoles,
  } = useGetCreatableRoles();

  useEffect(() => {
    getCreatableRoles();
  }, []);

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .required("First name is required")
      .max(150, "First name must be at most 150 characters")
      .matches(/^[A-Za-z ]+$/, "First name must contain only letters"),
    lastName: yup
      .string()
      .required("Last name is required")
      .max(150, "Last name must be at most 150 characters")
      .matches(/^[A-Za-z ]+$/, "Last name must contain only letters"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required")
      .max(150, "Email must be at most 150 characters"),
    role_slug: yup.string().required("Role is required"),
  });

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    role_slug: "",
  };

  const handleSubmit = async (values) => {
    await createMember(
      {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        role_slug: values.role_slug,
      },
      () => {
        setShowSuccessModal(true);
      }
    );
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const handleMemberList = () => {
    navigate("/school-admin/settings/members");
  };

  const handleAddMore = () => {
    formikRef.current?.resetForm();
    setShowSuccessModal(false);
  };

  return (
    <div>
      <div className="pb-2">
        <BackButton />
      </div>

      <TopImage imageSrc={Images.TopImage} text="Add Member" />

      <div className="custom-card mt-4">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <CustomInput
                label="First Name"
                name="firstName"
                type="text"
                placeholder="Enter first name"
                className="custom-primary-input"
                layout="stacked"
              />

              <CustomInput
                label="Last Name"
                name="lastName"
                type="text"
                placeholder="Enter last name"
                className="custom-primary-input"
                layout="stacked"
              />

              <CustomInput
                label="Email"
                name="email"
                type="email"
                placeholder="Enter email address"
                className="custom-primary-input"
                layout="stacked"
              />

              <CustomSelect
                label="Role"
                name="role_slug"
                className="custom-primary-input"
                layout="stacked"
                disabled={rolesLoading}
              >
                <option value="">
                  {rolesLoading ? "Loading roles..." : "Select Role"}
                </option>
                {roles?.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </CustomSelect>

              <div>
                {(createError || rolesError) && (
                  <div className="custom-error pb-2">
                    {createError || rolesError}
                  </div>
                )}

                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={isSubmitting || createLoading || rolesLoading}
                >
                  {isSubmitting || createLoading ? "Adding..." : "Add Member"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <SuccessModal
        showModal={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        title="Success"
        message="Member added successfully!"
        primaryButtonText="Add More"
        secondaryButtonText="Member List"
        onPrimaryClick={handleAddMore}
        onSecondaryClick={handleMemberList}
      />
    </div>
  );
};

export default AddMember;
